import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import _ from 'lodash'
import { format, compareDesc } from 'date-fns'

import { ReactComponent as GlassesSVG } from 'assets/svg/glasses.svg'
import { ReactComponent as LensSVG } from 'assets/svg/lens.svg'

import { Color, Gap, NINAL_SHORTER_NAME, Responsive, Size } from 'const'
import { Banner, Button, ChevronIcon, Logout, OldProduct } from 'components/shard'
import { Modal } from 'components/desktop/common/Modal'
import Alert from 'components/Alert'
import { Link } from 'react-router-dom'

import { useServiceHook } from 'hooks'
import { formatTZ } from 'utils/datetime'
import WrapperLink from 'components/shard/WrapperLink'

function ServiceDetail() {
  const {
    // service関連
    service,
    serviceErrors,
    getService,
    isServiceDetailLoading,
    resetErrors,

    // creditCard関連
    creditCard,
    creditCardErrors,
    getCreditCard,

    // モーダル関連
    modalDisplay,
    setModalDisplay,
    setContractId,
    close,
    link,

    // product
    goToProductInfo,
    toTanakaSpecialURL,
  } = useServiceHook()

  useEffect(() => {
    getService()
    getCreditCard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const headingElement = (
    <Heading>
      サービス内容やよくある質問は
      <HeadingLink as="div" role="button" tabIndex={0} onClick={toTanakaSpecialURL}>
        コチラ
      </HeadingLink>
    </Heading>
  )

  return (
    <React.Fragment>
      {headingElement}
      {isServiceDetailLoading && <ServiceDetailIsRequesting />}
      {(serviceErrors.length > 0 || creditCardErrors.length > 0) && !isServiceDetailLoading && (
        <Alert
          type="error"
          message="エラー"
          description={serviceErrors.concat(creditCardErrors).join('\n')}
          onClose={resetErrors}
        />
      )}
      {!_.isEmpty(service) && !isServiceDetailLoading && (
        <React.Fragment>
          <Modal
            title="クレジットカードの登録"
            text="クレジットカードを登録ください。"
            caution="プリペイドやデビットの場合、申込直後に請求される場合があります。"
            display={modalDisplay}
            overlayOnClick={close}
            buttonOnClick={link}
          />
          <ServiceDetailDisplay
            service={service}
            creditCard={creditCard}
            setModalDisplay={setModalDisplay}
            link={link}
            setContractId={setContractId}
            goToProductInfo={goToProductInfo}
          />
        </React.Fragment>
      )}
      <Container>
        <Banner />
        <Logout />
      </Container>
    </React.Fragment>
  )
}

function ServiceDetailIsRequesting() {
  return (
    <IsRequestingWrapper>
      <IsRequesting>現在ご利用状況、およびお支払い状況を取得中</IsRequesting>
    </IsRequestingWrapper>
  )
}

function ServiceDetailDisplay({ service, creditCard, setModalDisplay, link, setContractId, goToProductInfo }) {
  useEffect(() => {
    if (!_.isEmpty(service)) {
      // モーダルのリンク先セット
      setContractId(service.dev_contractid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service])

  // 概要
  const info = service.ninal_subscription_application
  const serviceName = NINAL_SHORTER_NAME[service.dev_service_name]
  const stateNo = service.dev_state_no

  // 利用者契約者
  const contractor = info.contractor
  const user = info.user

  // 購入明細
  const newItem = info.new_item
  const oldItems = info.old_items
  const willReturnItem = info.will_return_item

  // 判定分
  const isNewFrameExist = newItem.frames.length > 0
  const isNewLensExist = newItem.lenses.length > 0
  const isOldItemsExist = oldItems.length > 0
  const newItemArray = _.flatMap(_.values(newItem))
  const willReturnItemArray = _.flatMap(_.values(willReturnItem))
  const isItemsExist = newItemArray.length > 0 || willReturnItemArray.length > 0

  // credit card
  const isCreditCartExist = !_.isEmpty(creditCard)

  const orders = {
    右: 2,
    左: 3,
  }

  const groupOldItems = isOldItemsExist
    ? _.chain(
        oldItems
          .map((it) => ({ ...it, dev_return_date_fm: formatTZ(it.dev_return_date) }))
          .sort((a, b) => compareDesc(new Date(a.dev_return_date), new Date(b.dev_return_date)))
      )
        .groupBy(
          (item) =>
            `${item.dev_return_date_fm}_${item.dev_return_code || item.dev_return_reason.replace('NINAL', '')}_${
              item.dev_box_number
            }`
        )
        .map((items, key) => {
          const keys = key.split('_')

          return {
            dev_return_date: keys[0],
            dev_return_code: keys[1],
            dev_box_number: keys[2],
            products: items
              .map((item) => ({ ...item, pos: item.dev_rl ? orders[item.dev_rl] : 1 }))
              .sort((a, b) => a.pos - b.pos),
          }
        })
        .value()
    : []

  // ご利用中の貸出日算出(現在ご利用中の商品の中で一番最新の貸出日を取得する)
  const salesDate = (() => {
    const concatenatedItems = newItem.frames.concat(newItem.lenses)

    if (concatenatedItems.length > 0) {
      // 全部nullなら空文字にする
      if (concatenatedItems.every((item) => item.dev_delivery_date === null)) {
        return
      }

      const dateArray = concatenatedItems.map((item) => new Date(item.dev_delivery_date))
      dateArray.sort(compareDesc)

      return format(dateArray[0], 'yyyy年M月d日貸出')
    }

    return ''
  })()

  const newItemsPurchaseDetailIds = _.flatMap(_.values(newItem)).map((item) => item.dev_purchase_detailid)

  const getOldItemsPurchaseDetailsId = (items) => {
    return items.map((item) => item.dev_purchase_detailid)
  }

  const canSubscriptionAvailableShow =
    serviceName !== 'NINAL step' && (stateNo === 277670000 || (stateNo === 277670003 && isItemsExist))

  const subscriptionAvailableText =
    (isNewFrameExist || isNewLensExist) && isCreditCartExist ? `${info.available_num}回` : '-'

  return (
    <React.Fragment>
      <ServiceBlock>
        <Title>
          <GlassesSVG />
          <ServiceName>{serviceName}</ServiceName>
        </Title>
        <ContractNum>契約番号: {info.dev_contract_no ? info.dev_contract_no : '-'}</ContractNum>
        <ListLabelArea>
          <ListLabel>お客様情報</ListLabel>
        </ListLabelArea>
        <List>
          <NameBox label="ご利用者氏名" name={user.lastname + ` ` + user.firstname} />
          <NameBox label="ご契約者氏名" name={contractor.lastname + ` ` + contractor.firstname} />
        </List>
        {info.is_contractor && (
          <PayBlock>
            <ListLabelAreaFlex>
              <ListLabel>お支払い方法</ListLabel>
            </ListLabelAreaFlex>
            <PayArea>
              <PayLabel>クレジットカード</PayLabel>
              {!isCreditCartExist ? (
                <PayCaution>登録されていません</PayCaution>
              ) : (
                <PayNumber>
                  <span className="cardno">{creditCard.card_no}</span>
                  <span className="expires">
                    （ 有効期限：{creditCard.month}/{creditCard.year} ）
                  </span>
                </PayNumber>
              )}
            </PayArea>
            <Separator />
            <PayButtonWrapper>
              <Link to={`/pay/${service.dev_contractid}`}>
                <Button fontSize="18px" type="button" primary>
                  お支払い状況を確認する
                </Button>
              </Link>
            </PayButtonWrapper>
            <PayButtonWrapper>
              <Button
                fontSize="18px"
                type="button"
                primary
                onClick={_.isEmpty(creditCard) ? () => setModalDisplay(true) : link}
              >
                クレジットカードを{_.isEmpty(creditCard) ? '登録' : '変更'}する
              </Button>
            </PayButtonWrapper>
          </PayBlock>
        )}
        <ListLabelArea>
          <ListLabel>契約情報</ListLabel>
        </ListLabelArea>
        <List>
          <ContractBox
            label="契約開始日"
            date={info.dev_open_date ? format(new Date(info.dev_open_date), 'yyyy年M月d日') : '-'}
          />
          <ContractBox
            label="契約終了日"
            date={
              info.dev_close_date
                ? format(new Date(info.dev_close_date), 'yyyy年M月d日')
                : info.dev_will_close_date
                ? format(new Date(info.dev_will_close_date), 'yyyy年M月d日')
                : '-'
            }
          />
          {canSubscriptionAvailableShow && (
            <ContractBox label="残りの交換回数" date={subscriptionAvailableText} labelFw={500} dateFs={Size.FONT.BIG} />
          )}
        </List>
        <ListLabelAreaFlex>
          <ListLabel>現在ご利用中のフレーム</ListLabel>
        </ListLabelAreaFlex>
        {isNewFrameExist || isNewLensExist ? (
          <NewProductBox onClick={() => goToProductInfo(newItemsPurchaseDetailIds)}>
            <ProductList>
              {isNewFrameExist &&
                _.map(newItem.frames, (frame) => {
                  return (
                    <ProductBox
                      key={frame.dev_purchase_detailid}
                      isLens={false}
                      text1={'フレーム'}
                      text2={`${frame.dev_brand} ${frame.abbreviation || ''}`}
                    />
                  )
                })}
              {isNewLensExist &&
                _.map(newItem.lenses, (lens) => {
                  return (
                    <ProductBox
                      key={lens.dev_purchase_detailid}
                      isLens={true}
                      text1={lens.dev_rl === '左' ? 'レンズL' : 'レンズR'}
                      // text2={`${frame.dev_brand} ${frame.abbreviation} ${lens.dev_sph`}
                      text2={`${lens.dev_brand} ${lens.abbreviation || ''}`}
                    />
                  )
                })}
            </ProductList>
            {salesDate && <ProductDate>貸出日: {salesDate}</ProductDate>}
            <Separator />
            <div className="navigation">
              <ChevronIcon />
            </div>
          </NewProductBox>
        ) : (
          <ListNothing>現在ご利用中のフレーム/レンズはありません。</ListNothing>
        )}
        <ListLabelArea>
          <ListLabel>過去ご利用済みのフレーム/レンズ</ListLabel>
        </ListLabelArea>
        <GridBlock two={isOldItemsExist}>
          {isOldItemsExist ? (
            _.map(groupOldItems, (item, index) => {
              return (
                <OldProduct
                  key={index}
                  {...item}
                  onClick={() => {
                    const purchaseDetailIds = getOldItemsPurchaseDetailsId(item.products)
                    goToProductInfo(purchaseDetailIds)
                  }}
                />
              )
            })
          ) : (
            <ListNothing>過去ご利用済みのフレーム/レンズはありません。</ListNothing>
          )}
        </GridBlock>
      </ServiceBlock>
    </React.Fragment>
  )
}

const Heading = styled.div`
  margin-bottom: 30px;
  font-weight: 500;
  font-size: ${Size.FONT.BIG};
`

const HeadingLink = styled(WrapperLink)`
  cursor: pointer;
  display: inline-block;
  text-decoration: underline;
`

function NameBox({ label, name }) {
  return (
    <NameArea>
      <NameLabel>{label}</NameLabel>
      <Name>{name}</Name>
      <NameLinkArea />
    </NameArea>
  )
}

function ContractBox({ label, date, labelFw, dateFs }) {
  return (
    <ContractArea>
      <ContractLabel fw={labelFw}>{label}</ContractLabel>
      <ContractDate fs={dateFs}>{date}</ContractDate>
    </ContractArea>
  )
}

function ProductBox({ isLens, text1, text2, info1, info2 }) {
  return (
    <ProductArea>
      <SVG>{isLens ? <LensSVG /> : <GlassesSVG />}</SVG>
      {_.isEmpty(text2) ? (
        <ProductText>{text1}</ProductText>
      ) : (
        <>
          <ProductText1>{text1}</ProductText1>
          <ProductText2>{text2}</ProductText2>
        </>
      )}
      <ProductInfo1>{info1}</ProductInfo1>
      <ProductInfo2>{info2}</ProductInfo2>
    </ProductArea>
  )
}

const Container = styled.div`
  margin-top: 60px;
`

const ServiceBlock = styled.div`
  padding: 31px 0;
  border-radius: 10px;
  border: 1px solid ${Color.LINE.NORMAL};
  margin-bottom: 40px;
`
const IsRequestingWrapper = styled.div`
  border-radius: 10px;
  border: 1px solid ${Color.LINE.NORMAL};
  padding: 31px 0;
  margin-bottom: 60px;
`
const IsRequesting = styled.div`
  font-size: ${Size.FONT.REGULAR};
  margin-left: 24px;
`
const Title = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 17px;
`
const ServiceName = styled.div`
  font-size: ${Size.FONT.HEADLINE.LEARGE};
  text-align: center;
  margin-left: 10px;
  height: 40px;
  line-height: 40px;
  font-weight: 700;
`
const ContractNum = styled.div`
  font-size: ${Size.FONT.LG};
  margin-bottom: 31px;
  text-align: center;
  font-weight: 500;
`
// const CautionBox = styled.div`
//   margin-bottom: 25px;
//   text-align: left;
// `
// const Caution = styled.span`
//   font-size: ${Size.FONT.REGULAR};
//   padding: 1px 24px;
// `
const ListLabelArea = styled.div`
  background-color: ${Color.GREY};
  padding: 5px 20px;
  font-weight: 700;
  font-size: ${Size.FONT.HEADLINE.MEDIUM};
`
const ListLabelAreaFlex = styled.div`
  background-color: ${Color.GREY};
  padding: 5px 20px;
  display: flex;
  justify-content: space-between;
  font-weight: 700;
  font-size: ${Size.FONT.HEADLINE.MEDIUM};
`
const ListLabel = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  font-size: ${Size.FONT.BIGGER};
`
const List = styled.div`
  padding: 30px 24px;
`
const NameArea = styled.div`
  display: grid;
  grid-template-columns: 113px auto 20px 1fr 53px;
  grid-template-rows: 48px;
`
const NameLabel = styled.div`
  grid-column: 1;
  display: grid;
  align-items: center;
  font-weight: 700;
  font-size: ${Size.FONT.BIG};
`
const Name = styled.div`
  grid-column: 2;
  display: grid;
  align-items: center;
  font-size: ${Size.FONT.BIGGER};
  font-weight: 500;
`
const NameLinkArea = styled.div`
  grid-column: 4;
  display: grid;
  align-items: center;
`
const ContractArea = styled.div`
  display: grid;
  grid-template-columns: 113px 1fr;
  grid-template-rows: 48px;
  gap: 8px;
`
const ContractLabel = styled.div`
  grid-column: 1;
  display: grid;
  align-items: center;
  font-weight: ${(props) => props.fw || 700};
  font-size: ${Size.FONT.BIG};
`
const ContractDate = styled.div`
  grid-column: 2;
  display: grid;
  align-items: center;
  font-size: ${(props) => props.fs || Size.FONT.BIGGER};
  font-weight: 500;
`

const NewProductBox = styled.div`
  border-radius: 10px;
  border: 1px solid ${Color.LINE.NORMAL};
  margin: 16px 24px;
  cursor: pointer;
  position: relative;
  .navigation {
    position: absolute;
    right: 12px;
    top: 40%;
  }
`

const ProductList = styled.div`
  margin: 0 24px;
`
const ProductArea = styled.div`
  display: grid;
  grid-template-columns: 64px 1fr 140px;
  grid-template-rows: 30px 30px;
  margin: 18px 0;
  font-size: ${Size.FONT.REGULAR};
`
const Separator = styled.hr`
  border-top: 1px solid ${Color.LINE.SHADOW};
  border-bottom: 0;
  margin: 0 24px;
`
const SVG = styled.div`
  grid-column: 1 / 2;
  grid-row: 1 / 3;
  display: grid;
  align-items: center;
`
const ProductText = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
  display: grid;
  align-items: center;
`
const ProductText1 = styled.div`
  grid-column: 2 / 3;
  grid-row: 1;
  display: grid;
  align-items: center;
  font-size: ${Size.FONT.NORMAL};
  font-weight: 500;
`
const ProductText2 = styled.div`
  grid-column: 2 / 3;
  grid-row: 2;
  display: grid;
  align-items: center;
  font-weight: 700;
  font-size: ${Size.FONT.BIG};
`
const ProductInfo1 = styled.div`
  text-align: right;
  grid-column: 3 / 4;
  grid-row: 1;
  display: grid;
  align-items: center;
`
const ProductInfo2 = styled.div`
  text-align: right;
  grid-column: 3 / 4;
  grid-row: 2;
  display: grid;
  align-items: center;
`
const ProductDate = styled.div`
  text-align: left;
  margin-bottom: 25px;
  padding-left: 24px;
  font-size: ${Size.FONT.BIG};
  font-weight: 500;
`
const ListNothing = styled.div`
  font-size: ${Size.FONT.REGULAR};
  display: flex;
  padding: 28px 24px;
  justify-content: center;
  font-weight: 500;
`
const PayBlock = styled.div`
  padding-bottom: 7px;
`
const PayArea = styled.div`
  display: grid;
  grid-template-columns: 128px 1fr;
  grid-template-rows: 28px;
  padding: 22px 26px 60px;
  gap: 12px;
`
const PayLabel = styled.div`
  grid-column: 1;
  display: grid;
  align-items: center;
  font-weight: 700;
  font-size: ${Size.FONT.BIG};
`
const PayCaution = styled.div`
  grid-column: 2;
  display: grid;
  align-items: center;
`
const PayNumber = styled.div`
  grid-column: 2;
  font-size: ${Size.FONT.BIGGER};
  font-weight: 500;

  .cardno {
    display: inline-block;
  }

  .expires {
    font-size: ${Size.FONT.BIG};
    display: inline-block;
  }
`
const PayButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 15px;
  &:last-of-type {
    margin-bottom: 40px;
  }
`

// const DetailButton = styled.button`
//   cursor: pointer;
//   border: none;
//   background: none;
//   outline: none;
// `

ServiceDetailDisplay.propTypes = {
  service: PropTypes.object,
  creditCard: PropTypes.object,
  setModalDisplay: PropTypes.func,
  link: PropTypes.func,
  setContractId: PropTypes.func,
}
NameBox.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  link: PropTypes.string,
}
ContractBox.propTypes = {
  label: PropTypes.string,
  date: PropTypes.string,
  labelFw: PropTypes.number,
  dateFs: PropTypes.string,
}
ProductBox.propTypes = {
  isLens: PropTypes.bool,
  text1: PropTypes.string,
  text2: PropTypes.string,
  info1: PropTypes.string,
  info2: PropTypes.string,
}

const GridBlock = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.two ? 'repeat(2, 1fr)' : '1fr')};
  gap: ${Gap.LG};
  padding: 0 24px;
  margin-top: 24px;

  @media screen and (max-width: ${Responsive.MD.DOWN}px) {
    grid-template-columns: 1fr;
  }
`
export default ServiceDetail
